import React from "react";
import { Icon } from "@iconify/react";
import EmailPreview from "../../components/LaunchedTemplates/EmailPreview";
import ToggleButton from "../../components/ToggleButton/ToggleButton";

// API values stay as enums; only the visible label is human-friendly.
const notificationTypeLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

// Renders a single scheduled-event / launched-notification record.
// Works for both plain scheduled events (Main) and launched records
// (Explore) — the extra launch-metadata strip only renders when those
// fields are actually present on the event object.
const ScheduledEventCard = ({
  event,
  onReview,
  onDownload,
  // Shown only for templates already in use; sends the person to the copy
  // they downloaded earlier (Main tab).
  onViewUsed,
  // Main tab: marks the template the person was sent here to see.
  highlighted = false,
  isDownloading,
  displayMode = "gallery",
  reviewLabel = "Customize & Download",
  reviewIconOnly = false,
  onToggleStatus,
  isTogglingStatus = false,
  onDelete,
  isDeleting = false,
}) => {
  // The preview object contains the rendered, real-value version from the
  // marketplace API. Fall back to the editable source template for older API
  // responses that do not yet include it.
  const template = event.eventTemplatePreview ?? event.eventTemplate ?? {};

  const hasLaunchInfo =
    event.launchTemplateStatus != null ||
    event.launchDate != null ||
    event.launchTemplateVersionType != null;

  return (
    <div
      className={`scheduled-event-card scheduled-event-card--${displayMode}${highlighted ? " scheduled-event-card--highlighted" : ""}`}
    >
      <div className="scheduled-event-card__header">
        <span className="scheduled-event-card__task">{event.taskName}</span>
        <div className="scheduled-event-card__status-actions">
          {onToggleStatus && (
            <ToggleButton
              isOn={String(event.status).toUpperCase() === "ENABLED"}
              onToggle={() => !isTogglingStatus && onToggleStatus(event)}
            />
          )}
          {onReview && reviewIconOnly && (
            <button
              type="button"
              className="scheduled-event-card__review-button"
              onClick={() => onReview(event)}
              disabled={isDownloading}
              aria-label="Edit downloaded template"
              title="Edit"
            >
              <Icon icon="mdi:pencil-outline" />
            </button>
          )}
          {onDelete && reviewIconOnly && (
            <button
              type="button"
              className="scheduled-event-card__review-button scheduled-event-card__review-button--delete"
              onClick={() => onDelete(event)}
              disabled={isDeleting}
              aria-label="Delete downloaded template"
              title="Delete"
            >
              <Icon icon={isDeleting ? "mdi:loading" : "mdi:delete-outline"} />
            </button>
          )}
        </div>
      </div>

      <div className="scheduled-event-card__flow">
        <span>{event.fromEmailSource}</span>
        <Icon icon="mdi:arrow-right" />
        <span>{event.toEmailSourceFrom}</span>
      </div>

      {event.notificationTypes?.length > 0 && (
        <div className="notification-type-pills">
          {event.notificationTypes.map((nt) => (
            <span
              key={nt.notificationTypeId}
              className="notification-type-pill"
            >
              <Icon
                icon={
                  nt.notificationType === "EMAIL"
                    ? "mdi:email-outline"
                    : "mdi:bell-outline"
                }
              />
              {notificationTypeLabel(nt.notificationType)}
            </span>
          ))}
        </div>
      )}

      {hasLaunchInfo && (
        <div className="launch-info-strip">
          {event.launchTemplateStatus && (
            <span
              className={`badge badge--${String(event.launchTemplateStatus).toLowerCase().replace(/\s+/g, "_")}`}
            >
              {event.launchTemplateStatus}
            </span>
          )}
          {event.launchTemplateVersionType && (
            <span className="launch-info-strip__version">
              {event.launchTemplateVersionType}
            </span>
          )}
          {event.launchDate && (
            <span className="launch-info-strip__date">
              <Icon icon="mdi:calendar-clock-outline" />
              {new Date(event.launchDate).toLocaleDateString()}
            </span>
          )}
          {event.alreadyUsed != null && (
            <span
              className={`launch-info-strip__used ${
                event.alreadyUsed ? "launch-info-strip__used--yes" : ""
              }`}
            >
              <Icon
                icon={
                  event.alreadyUsed
                    ? "mdi:check-circle-outline"
                    : "mdi:circle-outline"
                }
              />
              {event.alreadyUsed
                ? Number(event.noOfTimeUsed) > 0
                  ? `Used · ${event.noOfTimeUsed} ${Number(event.noOfTimeUsed) === 1 ? "time" : "times"}`
                  : "Used"
                : "Unused"}
            </span>
          )}
        </div>
      )}

      <div className="template-preview template-preview--marketplace">
        <div className="template-preview__header">
          <span className="template-preview__label">
            Rendered template preview
          </span>
        </div>

        <div className="template-preview__full">
          {template.subject && (
            <div className="template-preview__row">
              <span className="template-preview__row-label">Email Subject</span>
              <span>{template.subject}</span>
            </div>
          )}
          {template.notificationHeading && (
            <div className="template-preview__row">
              <span className="template-preview__row-label">App Heading</span>
              <span>{template.notificationHeading}</span>
            </div>
          )}
          <EmailPreview
            body={template.body}
            htmlBody={template.htmlBody}
            label="Template preview"
            variant="marketplace"
          />
          {template.notificationMessage && (
            <div className="template-preview__row">
              <span className="template-preview__row-label">
                In-app message
              </span>
              <p>{template.notificationMessage}</p>
            </div>
          )}
        </div>
      </div>

      {(onDownload || onReview) && (
        <div className="marketplace-card-actions">
          {onDownload && (
            <button
              type="button"
              className="marketplace-card-actions__download"
              onClick={() => onDownload(event)}
              disabled={isDownloading}
            >
              <Icon
                icon={isDownloading ? "mdi:loading" : "mdi:download-outline"}
              />
              {isDownloading ? "Downloading..." : "Download"}
            </button>
          )}
          {onViewUsed && event.alreadyUsed && (
            <button
              type="button"
              className="marketplace-card-actions__view-used"
              onClick={() => onViewUsed(event)}
              disabled={isDownloading}
            >
              <Icon icon="mdi:open-in-new" />
              View used template
            </button>
          )}
          {onReview && !reviewIconOnly && (
            <button
              type="button"
              className="marketplace-card-actions__edit"
              onClick={() => onReview(event)}
              disabled={isDownloading}
            >
              <Icon icon="mdi:tune-variant" />
              {reviewLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduledEventCard;
